import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Layer, Popup, Source, useMap } from 'react-map-gl';
import type { MapLayerMouseEvent } from 'react-map-gl';
import type { Polygon, Position } from 'geojson';
import {
  CIRCLE_HANDLE_INDEX,
  MIN_CIRCLE_RADIUS_FT,
  PREVIEW_COLOR,
  circleDraftFeatures,
  circleFromPolygon,
  circleHandleFeatures,
  circlePolygon,
  distanceFt,
  draggedCircle,
  followDrag,
  radiusLabel,
} from './utils';
import type { Circle } from './types';
import styles from './create-area-map-overlays.module.scss';

const DRAFT_SOURCE = 'create-area-circle-draft';
const HANDLE_SOURCE = 'create-area-circle-handles';
const HANDLE_LAYER = 'create-area-circle-handle-points';

/** How near (px) a press must land to a circle's centre to move it. */
const CENTRE_HIT_PX = 10;
/** How near (px) a press must land to a circle's edge to resize it. */
const EDGE_HIT_PX = 8;
/** A press released within this (px) of where it went down is a click. */
const CLICK_SLOP_PX = 4;

/**
 * The circle's paint. Map Query's mock draws it in the preview blue at 0.18 with
 * white-ringed handles; the editor's in the area's colour at 0.2 with 6px handles.
 *
 * @param {'mapQuery' | 'editor'} look - Which mock to follow.
 * @param {string} color - The colour to draw in.
 * @return {object} The fill, line, point and handle paint.
 */
const circlePaint = (look: 'mapQuery' | 'editor', color: string) => ({
  fill: {
    'fill-color': color,
    'fill-opacity': look === 'editor' ? 0.2 : 0.18,
  },
  line: { 'line-color': color, 'line-width': 2, 'line-dasharray': [2, 1] },
  point: {
    'circle-radius': 5,
    'circle-color': color,
    'circle-stroke-color': '#ffffff',
    'circle-stroke-width': 2,
  },
  handle:
    look === 'editor'
      ? {
          'circle-radius': 6,
          'circle-color': color,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
        }
      : {
          'circle-radius': 5,
          'circle-color': '#ffffff',
          'circle-stroke-color': color,
          'circle-stroke-width': 2,
        },
});

interface CircleDrag {
  index: number;
  kind: 'move' | 'resize';
  circle: Circle;
}

/** A point on the map canvas, in pixels. */
interface PixelPoint {
  x: number;
  y: number;
}

/** The map calls the hit test needs. */
interface ProjectingMap {
  project: (lngLat: [number, number]) => PixelPoint;
}

/**
 * The drawn circle under a point, and whether it was its centre (move) or its
 * edge (resize), within a few pixels of either; the nearer wins.
 *
 * @param {ProjectingMap} map - The map, to measure in pixels.
 * @param {Array} circles - The drawn circles with their index in the drawn set.
 * @param {PixelPoint} point - The pointer.
 * @param {number} handleIndex - A ring vertex, to read the radius in pixels.
 * @return {CircleDrag | null} The circle and the edit it starts, or null.
 */
const circleHitAt = (
  map: ProjectingMap,
  circles: { index: number; circle: Circle }[],
  point: PixelPoint,
  handleIndex: number
): CircleDrag | null => {
  let best: (CircleDrag & { gap: number }) | null = null;
  for (const { index, circle } of circles) {
    const centre = map.project(circle.center as [number, number]);
    const rim = map.project(
      circlePolygon(circle.center, circle.radiusFt).coordinates[0][
        handleIndex
      ] as [number, number]
    );
    const radiusPx = Math.hypot(rim.x - centre.x, rim.y - centre.y);
    const fromCentre = Math.hypot(point.x - centre.x, point.y - centre.y);
    const fromEdge = Math.abs(fromCentre - radiusPx);
    const hits: (CircleDrag & { gap: number })[] = [];
    if (fromCentre <= CENTRE_HIT_PX) {
      hits.push({ index, kind: 'move', circle, gap: fromCentre });
    }
    if (fromEdge <= EDGE_HIT_PX) {
      hits.push({ index, kind: 'resize', circle, gap: fromEdge });
    }
    for (const hit of hits) {
      if (!best || hit.gap < best.gap) best = hit;
    }
  }
  return best && { index: best.index, kind: best.kind, circle: best.circle };
};

/**
 * Armed, the map is a drawing surface: a crosshair, and no zoom on a double click.
 *
 * @param {object | undefined} mapRef - The map.
 * @param {boolean} armed - Whether a click draws.
 */
const useDrawingSurface = (
  mapRef: ReturnType<typeof useMap>['current'],
  armed: boolean
) => {
  useEffect(() => {
    if (!mapRef || !armed) return undefined;
    const map = mapRef.getMap();
    const canvas = map.getCanvas();
    canvas.style.cursor = 'crosshair';
    map.doubleClickZoom.disable();
    return () => {
      map.doubleClickZoom.enable();
      canvas.style.cursor = '';
    };
  }, [mapRef, armed]);
};

/** What the component's state looks like to the map's handlers. */
interface CircleLatest {
  draft: Circle | null;
  drag: CircleDrag | null;
  polygons: Polygon[];
  circles: { index: number; circle: Circle }[];
  onPolygonsChange: (polygons: Polygon[]) => void;
  onDone?: () => void;
  armed: boolean;
}

/**
 * The circle's gestures on the map: draw (armed), pick up and put down, drag, and
 * Esc. Bound once per map; they read the component's latest state through a ref.
 *
 * @param {object} gestures - The map, the component's latest state, the ring
 * vertex the label and handle sit on, and the setters for the circle being drawn
 * and the one being edited.
 */
const useCircleGestures = ({
  mapRef,
  latest,
  handleIndex,
  setDraft,
  setDrag,
}: {
  mapRef: ReturnType<typeof useMap>['current'];
  latest: MutableRefObject<CircleLatest>;
  handleIndex: number;
  setDraft: Dispatch<SetStateAction<Circle | null>>;
  setDrag: Dispatch<SetStateAction<CircleDrag | null>>;
}) => {
  // A drag ends in a click on the same spot, which must not start a new circle.
  const justDragged = useRef(false);
  // A circle picked up by a click: the pointer carries it until the next click
  // puts it down. The click that picked it up is the press's own, so it is skipped.
  const pickedUp = useRef(false);
  const skipClick = useRef(false);

  useEffect(() => {
    if (!mapRef) return undefined;
    const map = mapRef.getMap();
    // Set while a handle is held; stops the window listeners.
    let stopDrag: (() => void) | null = null;

    /**
     * The drawn circle under a point, and the edit it starts.
     *
     * @param {PixelPoint} point - The pointer.
     * @return {CircleDrag | null} The hit, or null.
     */
    const circleAt = (point: PixelPoint) =>
      circleHitAt(map, latest.current.circles, point, handleIndex);

    /**
     * Puts an edited circle down in place of the one it started from.
     *
     * @param {number} index - The circle's part.
     * @param {Circle} circle - Where the edit left it.
     */
    const commitEdit = (index: number, circle: Circle) => {
      map.dragPan.enable();
      setDrag(null);
      pickedUp.current = false;
      if (circle.radiusFt < MIN_CIRCLE_RADIUS_FT) return;
      const current = latest.current;
      current.onPolygonsChange(
        current.polygons.map((polygon, i) =>
          i === index ? circlePolygon(circle.center, circle.radiusFt) : polygon
        )
      );
    };

    /**
     * Presses on a circle's centre or edge. Dragged, it follows the pointer and
     * lands on release; released where it was pressed, it is picked up instead,
     * and the next click puts it down — unless armed, when the click draws.
     *
     * @param {MapLayerMouseEvent} event - The press.
     */
    const handleMouseDown = (event: MapLayerMouseEvent) => {
      // A new press starts a new gesture. Mapbox fires no click after a press
      // that moved, so the last drag's mark is cleared here, not by a click.
      justDragged.current = false;
      if (latest.current.draft || pickedUp.current) return;
      const hit = circleAt(event.point);
      if (!hit) return;
      const { index, kind, circle } = hit;
      event.preventDefault();
      map.dragPan.disable();
      setDrag(hit);
      const start = event.point;
      // Followed on the window, so a release anywhere ends it; sized from where
      // the button came up, so the circle ends under the pointer.
      stopDrag = followDrag(map, {
        move: (pointer) =>
          setDrag({
            index,
            kind,
            circle: draggedCircle(circle, kind, pointer),
          }),
        end: (pointer) => {
          stopDrag = null;
          const at = map.project(pointer as [number, number]);
          if (Math.hypot(at.x - start.x, at.y - start.y) < CLICK_SLOP_PX) {
            map.dragPan.enable();
            if (latest.current.armed) {
              // Armed, a click draws, even on a drawn circle: overlapping
              // circles start there. The click that follows sets the centre.
              setDrag(null);
              return;
            }
            // A click on the circle: carried by the pointer from here.
            pickedUp.current = true;
            skipClick.current = true;
            return;
          }
          justDragged.current = true;
          commitEdit(index, draggedCircle(circle, kind, pointer));
        },
      });
    };

    /**
     * Sizes the circle being drawn, or carries the one picked up. Unarmed, the
     * cursor says when a circle can be picked up.
     *
     * @param {MapLayerMouseEvent} event - The move.
     */
    const handleMouseMove = (event: MapLayerMouseEvent) => {
      const pointer: Position = [event.lngLat.lng, event.lngLat.lat];
      const current = latest.current;
      if (current.draft) {
        setDraft({
          ...current.draft,
          radiusFt: distanceFt(current.draft.center, pointer),
        });
        return;
      }
      if (pickedUp.current && current.drag) {
        const { index, kind, circle } = current.drag;
        setDrag({ index, kind, circle: draggedCircle(circle, kind, pointer) });
        return;
      }
      if (!current.armed && !current.drag) {
        const hit = circleAt(event.point);
        map.getCanvas().style.cursor = hit
          ? hit.kind === 'move'
            ? 'move'
            : 'ew-resize'
          : '';
      }
    };

    /**
     * Puts down a picked-up circle. Armed, the first click sets a new circle's
     * centre and the second its radius.
     *
     * @param {MapLayerMouseEvent} event - The click.
     */
    const handleClick = (event: MapLayerMouseEvent) => {
      if (justDragged.current) {
        justDragged.current = false;
        return;
      }
      if (skipClick.current) {
        skipClick.current = false;
        return;
      }
      const pointer: Position = [event.lngLat.lng, event.lngLat.lat];
      const current = latest.current;
      if (pickedUp.current && current.drag) {
        const { index, kind, circle } = current.drag;
        commitEdit(index, draggedCircle(circle, kind, pointer));
        return;
      }
      if (!current.armed) return;
      if (!current.draft) {
        setDraft({ center: pointer, radiusFt: 0 });
        return;
      }
      const radiusFt = distanceFt(current.draft.center, pointer);
      setDraft(null);
      if (radiusFt >= MIN_CIRCLE_RADIUS_FT) {
        current.onPolygonsChange([
          ...current.polygons,
          circlePolygon(current.draft.center, radiusFt),
        ]);
      }
      current.onDone?.();
    };

    /**
     * Esc drops the circle in progress, or puts an edited one back.
     *
     * @param {KeyboardEvent} event - The key press.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (!latest.current.draft && !latest.current.drag) return;
      event.stopPropagation();
      setDraft(null);
      if (latest.current.draft) latest.current.onDone?.();
      if (latest.current.drag) {
        stopDrag?.();
        stopDrag = null;
        pickedUp.current = false;
        map.dragPan.enable();
        setDrag(null);
      }
    };

    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('click', handleClick);
    // Heard in the capture phase and stopped while there is something to cancel,
    // so Esc drops the shape rather than also closing the modal or panel around
    // the map. With nothing in progress it passes on and closes them as usual.
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      stopDrag?.();
      map.off('click', handleClick);
      window.removeEventListener('keydown', handleKeyDown, true);
      map.dragPan.enable();
    };
  }, [mapRef, handleIndex, latest, setDraft, setDrag]);
};

export interface DrawCircleControlProps {
  /** Every drawn shape; a committed circle is appended as its polygon. */
  polygons: Polygon[];
  onPolygonsChange: (polygons: Polygon[]) => void;
  /** Which mock's circle to draw: Map Query's (the default) or the editor's. */
  look?: 'mapQuery' | 'editor';
  /** The colour to draw in; the preview blue unless the editor passes the area's. */
  color?: string;
  /** Called once a circle is placed or the one in progress is cancelled. */
  onDone?: () => void;
  /**
   * Whether a click starts a new circle. Unarmed, the drawn circles can still be
   * picked up and edited; nothing new is drawn. Defaults to armed.
   */
  armed?: boolean;
}

/**
 * Story 3's circle, as Map Query's mock draws it: a click sets the centre, moving
 * sizes the radius with a "Radius: N ft" label, a second click sets it, and Esc
 * cancels. A circle is kept as its polygon, so the drawn set needs no new shape;
 * {@link circleFromPolygon} reads it back, which is what lets an existing circle be
 * re-edited with the same gesture: click its centre to move it or its edge to
 * resize it, move, and click to put it down (or press and drag, as before).
 *
 * @param {DrawCircleControlProps} props - The drawn set and its setter.
 * @return {JSX.Element} The draft, handles and label layers.
 */
export function DrawCircleControl({
  polygons,
  onPolygonsChange,
  look = 'mapQuery',
  color = PREVIEW_COLOR,
  onDone,
  armed = true,
}: DrawCircleControlProps): JSX.Element {
  // The editor labels and resizes at the east handle; Map Query at the north.
  const handleIndex = look === 'editor' ? 0 : CIRCLE_HANDLE_INDEX;
  const paint = useMemo(() => circlePaint(look, color), [look, color]);
  const { current: mapRef } = useMap();
  const [draft, setDraft] = useState<Circle | null>(null);
  const [drag, setDrag] = useState<CircleDrag | null>(null);

  const circles = useMemo(
    () =>
      polygons.flatMap((polygon, index) => {
        const circle = circleFromPolygon(polygon);
        return circle ? [{ index, circle }] : [];
      }),
    [polygons]
  );

  // The map's handlers are bound once per map; they read the latest values here.
  const latest = useRef({
    draft,
    drag,
    polygons,
    circles,
    onPolygonsChange,
    onDone,
    armed,
  });
  useLayoutEffect(() => {
    latest.current = {
      draft,
      drag,
      polygons,
      circles,
      onPolygonsChange,
      onDone,
      armed,
    };
  }, [draft, drag, polygons, circles, onPolygonsChange, onDone, armed]);
  useDrawingSurface(mapRef, armed);
  useCircleGestures({ mapRef, latest, handleIndex, setDraft, setDrag });

  // The circle being sized: the one in progress, or the one being dragged.
  const live = drag?.circle ?? draft;
  const labelAt =
    live && live.radiusFt > 0
      ? circlePolygon(live.center, live.radiusFt).coordinates[0][handleIndex]
      : undefined;

  const draftData = circleDraftFeatures(live, handleIndex);
  // Handles for the circles already drawn, hidden while one is being drawn.
  const handleData = circleHandleFeatures(
    draft ? [] : circles.filter(({ index }) => index !== drag?.index),
    handleIndex
  );

  return (
    <>
      <Source id={DRAFT_SOURCE} type="geojson" data={draftData}>
        <Layer
          id={`${DRAFT_SOURCE}-fill`}
          type="fill"
          filter={['==', ['geometry-type'], 'Polygon']}
          paint={paint.fill}
        />
        <Layer
          id={`${DRAFT_SOURCE}-line`}
          type="line"
          filter={['==', ['geometry-type'], 'Polygon']}
          paint={paint.line}
        />
        <Layer
          id={`${DRAFT_SOURCE}-points`}
          type="circle"
          filter={['==', ['geometry-type'], 'Point']}
          paint={paint.point}
        />
      </Source>
      <Source id={HANDLE_SOURCE} type="geojson" data={handleData}>
        <Layer id={HANDLE_LAYER} type="circle" paint={paint.handle} />
      </Source>
      {live && labelAt && live.radiusFt > 0 && (
        <Popup
          longitude={labelAt[0]}
          latitude={labelAt[1]}
          offset={12}
          closeButton={false}
          closeOnClick={false}
          className={styles.radiusLabel}
        >
          {radiusLabel(live.radiusFt)}
        </Popup>
      )}
    </>
  );
}
